import { Execution, ExecutionLogEntry } from '../types/attacks';
import {
  OperationalError,
  OperationalErrorSeverity,
  OperationalErrorSource,
} from '../types/operationalErrors';
import { parseToDate } from './dateUtils';

/** Raw row from `GET /bots/errors` (`api.getBotManagerErrors`). */
export interface RawBotError {
  id: string;
  bot_id?: string | null;
  tenant_id?: string | null;
  region_name?: string | null;
  action: string;
  error_message: string;
  /** Stored as a JSON *string* in `bot_manager_errors.details` and returned verbatim. */
  details?: string | Record<string, unknown> | null;
  retry_count?: number | null;
  status?: string | null;
  created_at: string;
  last_attempt_at?: string | null;
}

/** Raw row from `GET /transactions/errors` (`api.getTransactionErrors`). */
export interface RawTransactionError {
  id: string;
  transaction_id: string;
  tenant_id?: string | null;
  scenario_id?: string | null;
  region_name?: string | null;
  action?: string | null;
  error_message: string;
  /** Native JSONB — already an object by the time it reaches us. */
  details?: Record<string, unknown> | null;
  affected_bots?: number | null;
  status?: string | null;
  created_at: string;
}

/**
 * Substrings that mark a failure the platform recovers from on its own: the region ran out of
 * capacity, the cloud account hit a quota, the API throttled us. Operators want to see these but
 * they are not "something is broken" — matching `ProviderErrorsPanel`'s classification.
 */
const TRANSIENT_MARKERS = [
  'quota',
  'capacity',
  'exhausted',
  'ratelimit',
  'rate limit',
  'throttl',
  'insufficient',
  'try again',
  'temporarily',
  'timeout',
  'timed out',
];

export const classifySeverity = (
  message: string,
  action?: string | null
): OperationalErrorSeverity => {
  const haystack = `${message || ''} ${action || ''}`.toLowerCase();
  return TRANSIENT_MARKERS.some((marker) => haystack.includes(marker)) ? 'warning' : 'error';
};

/**
 * `bot_manager_errors.details` is a JSON string, `transaction_errors.details` is JSONB, and either
 * can be null or (for legacy rows) a non-JSON string. Always hand the UI an object or null.
 */
export const parseErrorDetails = (
  details: string | Record<string, unknown> | null | undefined
): Record<string, unknown> | null => {
  if (details === null || details === undefined) return null;
  if (typeof details === 'object') {
    return Array.isArray(details) ? { items: details } : (details as Record<string, unknown>);
  }
  const text = String(details).trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    if (parsed === null) return null;
    if (typeof parsed === 'object') {
      return Array.isArray(parsed) ? { items: parsed } : (parsed as Record<string, unknown>);
    }
    return { value: parsed };
  } catch {
    // Legacy rows stored a bare message rather than JSON — keep it visible instead of dropping it.
    return { raw: text };
  }
};

const toMs = (iso: string | null | undefined): number => {
  const parsed = parseToDate(iso ?? null);
  return parsed ? parsed.getTime() : NaN;
};

export const normalizeBotError = (raw: RawBotError): OperationalError => {
  // `last_attempt_at` is the latest retry; fall back to creation for rows that never retried.
  const occurredAt = raw.last_attempt_at || raw.created_at;
  return {
    id: `bot:${raw.id}`,
    source: 'bot',
    severity: classifySeverity(raw.error_message, raw.action),
    action: raw.action || 'unknown',
    message: raw.error_message || 'Unknown bot error',
    occurredAt,
    occurredAtMs: toMs(occurredAt),
    regionName: raw.region_name ?? null,
    status: raw.status ?? null,
    retryCount: raw.retry_count ?? null,
    botId: raw.bot_id ?? null,
    details: parseErrorDetails(raw.details),
  };
};

export const normalizeTransactionError = (raw: RawTransactionError): OperationalError => ({
  id: `transaction:${raw.id}`,
  source: 'transaction',
  severity: classifySeverity(raw.error_message, raw.action),
  action: raw.action || 'transaction',
  message: raw.error_message || 'Unknown transaction error',
  occurredAt: raw.created_at,
  occurredAtMs: toMs(raw.created_at),
  regionName: raw.region_name ?? null,
  status: raw.status ?? null,
  affectedBots: raw.affected_bots ?? null,
  transactionId: raw.transaction_id,
  scenarioId: raw.scenario_id ?? null,
  details: parseErrorDetails(raw.details),
});

const startFailedReason = (entry: ExecutionLogEntry): string | null => {
  const reason = entry.details?.reason;
  if (typeof reason === 'string' && reason.trim()) return reason.trim();
  return null;
};

/**
 * Executions have no `error` column: a failed start writes a `start_failed` entry into
 * `execution_log` with `details.reason` and flips `status` to `failed`. Surface one error per
 * `start_failed` entry, plus a synthetic one for executions that failed without leaving a reason
 * (e.g. a run marked failed by an operator or by cleanup).
 */
export const normalizeExecutionErrors = (execution: Execution): OperationalError[] => {
  const log = Array.isArray(execution.execution_log) ? execution.execution_log : [];
  const failures = log.filter((entry) => entry?.event_type === 'start_failed');

  const errors = failures.map((entry, index) => {
    const occurredAt = entry.timestamp || execution.updated_at || execution.created_at;
    const reason = startFailedReason(entry);
    return {
      id: `execution:${execution.id}:${index}`,
      source: 'execution' as OperationalErrorSource,
      severity: classifySeverity(reason || '', 'start_failed'),
      action: 'start_failed',
      message: reason || 'Scenario failed to start',
      occurredAt,
      occurredAtMs: toMs(occurredAt),
      regionName: entry.region_name ?? null,
      status: execution.status,
      scenarioId: execution.scenario_id,
      executionId: execution.id,
      details: (entry.details as Record<string, unknown> | null | undefined) ?? null,
    };
  });

  if (errors.length === 0 && execution.status === 'failed') {
    const occurredAt = execution.completed_at || execution.updated_at || execution.created_at;
    errors.push({
      id: `execution:${execution.id}:failed`,
      source: 'execution',
      severity: 'error',
      action: 'execution_failed',
      message: `Execution failed${execution.scenario?.name ? ` — ${execution.scenario.name}` : ''}`,
      occurredAt,
      occurredAtMs: toMs(occurredAt),
      regionName: null,
      status: execution.status,
      scenarioId: execution.scenario_id,
      executionId: execution.id,
      details: null,
    });
  }

  return errors;
};

/**
 * Merge the three feeds into one list, newest first. Rows whose timestamp the backend could not
 * produce sort last rather than poisoning the comparison with `NaN`.
 */
export const mergeOperationalErrors = (
  ...groups: OperationalError[][]
): OperationalError[] => {
  const merged = ([] as OperationalError[]).concat(...groups);
  const byId = new Map<string, OperationalError>();
  merged.forEach((error) => {
    if (!byId.has(error.id)) byId.set(error.id, error);
  });
  return Array.from(byId.values()).sort((a, b) => {
    const aMs = Number.isNaN(a.occurredAtMs) ? -Infinity : a.occurredAtMs;
    const bMs = Number.isNaN(b.occurredAtMs) ? -Infinity : b.occurredAtMs;
    return bMs - aMs;
  });
};

export const SOURCE_LABELS: Record<OperationalErrorSource, string> = {
  bot: 'Bots',
  transaction: 'Transactions',
  execution: 'Executions',
};

export const countBySource = (
  errors: OperationalError[]
): Record<OperationalErrorSource, number> => {
  const counts: Record<OperationalErrorSource, number> = { bot: 0, transaction: 0, execution: 0 };
  errors.forEach((error) => {
    counts[error.source] += 1;
  });
  return counts;
};

/** Compact "3m ago" label; absolute time stays available in a tooltip. */
export const relativeTimeLabel = (iso: string | null | undefined, nowMs: number): string => {
  const then = toMs(iso);
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};
